import { useAppSelector } from "../../../../../../../../stores/hooks/hooks.ts";
import { fullFormatNumbers } from "../../../../../../../../helpers/functions/formatNumber.ts";
import { BarDatum, ResponsiveBar } from "@nivo/bar";
import { useMemo, useState } from "react";
import {
  COMMUNITY_INSIGHTS_CHARTS_COLOR,
  KEY_TO_NAME_MAP,
} from "../../data/communityInsightsData.ts";
import { RevCard, RevEmptyState, RevTooltip } from "@revmatics/catalyst";

export const BarChart = () => {
  const [emptyState, setEmptyState] = useState(false);
  const communityAggregate = useAppSelector(
    (state) => state.discoverCommunities.communityAggregate,
  );

  const result: BarDatum[] = useMemo(() => {
    if (communityAggregate && communityAggregate.bar_chart.length > 0) {
      setEmptyState(false);
      return communityAggregate.bar_chart;
    } else {
      setEmptyState(true);
      return [];
    }
  }, [communityAggregate]);

  return (
    <RevCard>
      <div>
        <h6>Community Size and Platforms</h6>
      </div>
      <div className="h-[364px] relative">
        {emptyState && (
          <RevEmptyState
            text="If you’re seeing this message, we don’t have enough data to complete these views."
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        )}
        <ResponsiveBar
          data={result}
          indexBy="platform_name"
          keys={["HIGH", "MEDIUM", "LOW"]}
          margin={{ top: 5, right: 0, bottom: 90, left: 55 }}
          axisBottom={{
            legend: "Platforms",
            legendOffset: 80,
            style: {
              ticks: {
                text: {
                  display: emptyState ? "none" : "block",
                  width: "20%",
                  fill: "#808092",
                  fontFamily: "Roboto",
                  fontSize: "12px",
                  transform: "translate(16px, 32px) rotate(30deg)",
                },
              },
              legend: {
                text: {
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "bold",
                  fill: "#808092",
                },
              },
            },

            tickPadding: 4,
            tickSize: 0,
          }}
          axisLeft={{
            legend: "Size",
            format: (value) => {
              return `${fullFormatNumbers({ value, noFormatNumber: true, shortenValue: 1e3 })}`;
            },
            style: {
              ticks: {
                text: {
                  fill: "#808092",
                  fontFamily: "Roboto",
                  fontSize: "12px",
                },
              },
              legend: {
                text: {
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: "bold",
                  fill: "#808092",
                },
              },
            },
            legendOffset: -48,
            tickPadding: 4,
            tickSize: 0,
          }}
          borderWidth={2}
          borderColor="#fff"
          enableLabel={false}
          enableGridX={false}
          enableGridY={false}
          isInteractive={true}
          tooltip={(value) => (
            <RevTooltip
              position="bottom"
              className="min-w-[180px] max-w-[240px] z-[9999]"
              wrapperClassName="p-0 overflow-hidden bg-white shadow-elevation-2"
            >
              <div className="flex flex-col bg-white">
                <div className="mx-3 py-1.5 border-b border-monochrome-200">
                  <p className="typography-body-bold-xs text-monochrome-700 text-nowrap">
                    {value.indexValue.toString()}
                  </p>
                </div>

                <div className="px-3 py-2 flex flex-col gap-1.5">
                  {Object.keys(value.data).map((item, index) => {
                    if (item === "platform_name") return null;

                    return (
                      <div
                        className="grid grid-cols-[8px_1fr] gap-2 items-center"
                        key={`${item}-${value.index}-${index}`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            COMMUNITY_INSIGHTS_CHARTS_COLOR[index]?.bg ||
                            "bg-monochrome-300"
                          }`}
                        />

                        <div className="flex justify-between gap-4">
                          <span className="typography-body-xs text-monochrome-600 lowercase">
                            {KEY_TO_NAME_MAP[item] || item}:
                          </span>
                          <span className="typography-body-bold-xs text-monochrome-900">
                            {fullFormatNumbers({ value: value.data[item] })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </RevTooltip>
          )}
          colors={COMMUNITY_INSIGHTS_CHARTS_COLOR.map(({ color }) => color)}
        />
      </div>
      <div className="flex flex-wrap gap-4 items-center">
        {["CORE", "ADJACENT", "EXPANSION"].map((item, index) => {
          return (
            <div
              className="flex items-center gap-2"
              key={`rev-legend-charts-bar-discover-${index}`}
            >
              <div
                className={`w-3 h-3 rounded-xs ${COMMUNITY_INSIGHTS_CHARTS_COLOR[index].bg}`}
              />
              <p className="typography-body-xs text-monochrome-900 capitalize">
                {item.toLowerCase()}
              </p>
            </div>
          );
        })}
      </div>
    </RevCard>
  );
};
